import type {
  UseLiftPlanUELogicProps as IndividualPlanProps,
  SaveLiftPlanUEData,
} from '@/components/common/projects/lift-plan-ue/logic/useLiftPlanUELogic';
import type { LiftPlan } from '@/components/common/projects/lift-plan-ue/types';
import type { SessionUser } from 'cesg-shared/dist/models';
import { computed, reactive, toRef } from 'vue';
import type { ButtonState } from './useLiftPlanUEListPermissions';
import { useLiftPlanUEListPermissions } from './useLiftPlanUEListPermissions';

export type UseLiftPlanUEListLogicProps = {
  user: SessionUser;
  projectId: string;
  initialLiftPlans: (LiftPlan & { id: string })[];
  isCreating: boolean;
  isSavingId?: string;
};

export type LiftPlanUEListEmit = {
  (e: 'create'): void;
  (e: 'delete', liftPlanId: string): void;
  (e: 'save', liftPlanId: string, data: SaveLiftPlanUEData): Promise<void>;
};

type LiftPlanUEItem = IndividualPlanProps & {
  id: string;
  onDelete: () => void;
  onSave: (data: SaveLiftPlanUEData) => Promise<void>;
};

type UseLiftPlanUEListLogicReturn = {
  state: {
    liftPlans: LiftPlanUEItem[];
    mode: {
      addUEButton: ButtonState;
    };
  };
  action: {
    addUE: () => void;
  };
};

export const useLiftPlanUEListLogic = (
  props: UseLiftPlanUEListLogicProps,
  emit: LiftPlanUEListEmit,
): UseLiftPlanUEListLogicReturn => {
  const { user, projectId } = props;
  const permissions = useLiftPlanUEListPermissions({ user, projectId });

  const initialLiftPlans = toRef(props, 'initialLiftPlans');

  const liftPlans = computed(() =>
    initialLiftPlans.value.map((p) => ({
      ...p,
      entries: p.entries.map((e) => ({ ...e })),
      subtotalRows: p.subtotalRows.map((r) => ({ ...r })),
    })),
  );

  return {
    state: reactive({
      liftPlans: computed(() =>
        liftPlans.value.map(({ id, ...p }) => ({
          id,
          initialLiftPlan: {
            ...p,
            id,
          },
          isSaving: props.isSavingId === id,
          user: props.user,
          projectId: props.projectId,
          onDelete: () => emit('delete', id),
          onSave: (data: SaveLiftPlanUEData) => emit('save', id, data),
        })),
      ),
      mode: computed(() => ({
        addUEButton: permissions.value.addUEButton,
      })),
    }),
    action: {
      addUE: () => {
        if (props.isCreating) return;
        emit('create');
      },
    },
  };
};
